import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/api";
import { format, parseISO } from "date-fns";
import {
  Search,
  Calendar,
  Tag,
  CheckCircle2,
  Clock,
  ExternalLink,
  ChevronDown,
} from "lucide-react";

type PendingItem = { item: string; ticket_slug?: string };

type Session = {
  id: string;
  session_date: string;
  title: string;
  topics: string[];
  highlights: string[];
  completed: string[];
  pending: PendingItem[];
};

function SessionCard({ session }: { session: Session }) {
  const [expanded, setExpanded] = useState(false);

  // If no highlights yet, fall back to showing completed directly
  const hasHighlights = session.highlights?.length > 0;
  const hasCompleted = session.completed?.length > 0;
  const hasPending = session.pending?.length > 0;
  const showExpandToggle = hasHighlights && hasCompleted;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
      {/* Title */}
      <h3 className="font-semibold text-gray-900 text-sm leading-snug">
        {session.title}
      </h3>

      {/* Topics */}
      {session.topics?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {session.topics.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 rounded-full font-medium"
            >
              <Tag className="h-2.5 w-2.5" />
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Highlights — short summary bullets (always visible) */}
      {hasHighlights && (
        <ul className="space-y-1.5">
          {session.highlights.map((item, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      )}

      {/* Pending items — always visible */}
      {hasPending && (
        <ul className="space-y-1.5">
          {session.pending.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
              <Clock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <span className="flex-1">
                {p.item}
                {p.ticket_slug && (
                  <Link
                    to={`/tickets/${p.ticket_slug}`}
                    className="inline-flex items-center gap-1 ml-2 text-xs text-sky-600 hover:text-sky-800 font-medium"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {p.ticket_slug}
                  </Link>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Fallback: if no highlights, show completed directly */}
      {!hasHighlights && hasCompleted && (
        <ul className="space-y-1.5">
          {session.completed.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      )}

      {/* Technical details toggle */}
      {showExpandToggle && (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 font-medium transition-colors"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
            {expanded ? "Hide technical steps" : "See technical steps"}
          </button>

          {expanded && (
            <ul className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
              {session.completed.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                  <span className="text-gray-300 mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function groupByDate(sessions: Session[]): { date: string; items: Session[] }[] {
  const map = new Map<string, Session[]>();
  for (const s of sessions) {
    if (!map.has(s.session_date)) map.set(s.session_date, []);
    map.get(s.session_date)!.push(s);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
}

export function SessionsPage() {
  const api = useApi();
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setSessions(null);
    (async () => {
      try {
        const url = debouncedQuery
          ? `/api/sessions?q=${encodeURIComponent(debouncedQuery)}`
          : "/api/sessions";
        const res = await api(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Session[];
        if (!cancelled) setSessions(data);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load sessions");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const groups = sessions ? groupByDate(sessions) : [];

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Sessions</h1>
        <p className="text-sm text-gray-500 mt-1">
          What was done and what&apos;s still pending — searchable across all sessions.
        </p>
      </div>

      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by topic, keyword, or ticket..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
        />
      </div>

      {error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      ) : sessions === null ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-36 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">
            {query ? `No sessions matching "${query}"` : "No sessions yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {groups.map((group) => (
            <div key={group.date}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                  {format(parseISO(group.date), "EEEE, MMMM d, yyyy")}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <div className="space-y-3">
                {group.items.map((s) => (
                  <SessionCard key={s.id} session={s} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
